'use client';

import clsx from 'clsx';
import { User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { PrefetchMode } from '@//hooks/useLinkPrefetch';
import { AppLink } from '../AppLink';
import { Container } from '../Container';
import { Spinner } from '../Spinner';
import styles from './Header.module.scss';

interface HeaderProps {
  logo: React.ReactNode;
  links?: {
    href: string;
    label: string;
  }[];
  controls?: React.ReactNode;
  search?: React.ReactNode;
}

export const Header = ({ logo, links, controls, search }: HeaderProps) => {
  const t = useTranslations('other.header');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerClass = clsx(styles.header, {
    [styles.headerScrolled]: isScrolled,
  });

  return (
    <header className={headerClass} data-testid="header">
      <Container size="max">
        <div className={styles.headerContent}>
          <AppLink
            href="/"
            className={styles.logo}
            data-testid="header-home-link"
            prefetch="viewport"
          >
            <span className="sr-only">{t('homeLink.label')}</span>
            {logo}
          </AppLink>
          {links && (
            <nav
              className={styles.nav}
              aria-label={t('mainNavigation.ariaLabel')}
              data-testid="header-main-navigation"
            >
              {links.map((link, index) => (
                <AppLink
                  key={link.href}
                  href={link.href}
                  className={styles.link}
                  data-testid={`header-main-navigation-link-${index + 1}`}
                  prefetch="viewport"
                >
                  {link.label}
                </AppLink>
              ))}
            </nav>
          )}
          {controls && <div className={styles.controls}>{controls}</div>}
          {search}
        </div>
      </Container>
    </header>
  );
};

// Control link used in the header

interface HeaderControlLinkProps {
  href: string;
  label: string;
  title: string;
  children: React.ReactNode;
  testId?: string;
  /** Prefetch policy. Default 'none' — explicit opt-in required. */
  prefetch?: PrefetchMode;
}

export const HeaderControlLink = ({
  href,
  label,
  title,
  children,
  testId,
  prefetch,
  ...props
}: HeaderControlLinkProps) => (
  <AppLink
    href={href}
    title={title}
    className={clsx(styles.control, styles.controlLink)}
    data-testid={testId}
    prefetch={prefetch}
    {...props}
  >
    <span className="sr-only">{label}</span>
    {children}
  </AppLink>
);

// Control Button used in the header

interface HeaderControlButtonProps {
  label: string;
  title: string;
  children: React.ReactNode;
  hiddenMd?: boolean;
  testId?: string;
  onClick?: () => void;
}

export const HeaderControlButton = ({
  label,
  title,
  children,
  testId,
  hiddenMd,
  onClick,
  ...props
}: HeaderControlButtonProps) => (
  <button
    type="button"
    title={title}
    className={clsx(styles.control, styles.controlButton, {
      [styles.controlButtonHiddenMd]: hiddenMd,
    })}
    data-testid={testId}
    onClick={onClick}
    {...props}
  >
    <span className="sr-only">{label}</span>
    {children}
  </button>
);

// Avatar button used in the header to trigger the profile menu

interface HeaderAvatarProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  title: string;
  loading: boolean;
  initials?: React.ReactNode;
  avatarAriaLabel: string;
  hasNotifications?: boolean;
  testId?: string;
}

export const HeaderAvatar = ({
  label,
  title,
  loading,
  initials,
  avatarAriaLabel,
  hasNotifications,
  testId,
  ...props
}: HeaderAvatarProps) => {
  const t = useTranslations('other.header');

  const _ariaLabel = hasNotifications
    ? `${avatarAriaLabel}. ${t('profileNavigation.triggerButton.avatarNotificationAriaLabel')}.`
    : avatarAriaLabel;

  return (
    <button
      type="button"
      title={title}
      className={styles.avatarButton}
      data-testid={testId}
      {...props}
    >
      <span className="sr-only">{label}</span>
      <span
        role="img"
        aria-label={_ariaLabel}
        className={clsx(styles.avatar, {
          [styles.avatarNotification]: hasNotifications,
        })}
      >
        {loading ? <Spinner size="text" /> : initials ? initials : <User role="presentation" />}
      </span>
    </button>
  );
};
